import { useInfiniteQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useRef, useState } from 'react';

import {
    PageSection,
    QUERY_KEYS,
    Profile,
    ASSET_TYPE,
    Asset,
    AdditionalItems,
    Channel,
} from '@24i/nxg-sdk-photon';
import { useContentData } from '@24i/nxg-sdk-smartott-shared/src/context/ContentData';
import { useFirebase } from '@24i/nxg-sdk-smartott-shared/src/context/Firebase';
import { log } from '@24i/nxg-core-utils/src/logger';

const INITIAL_STALE_TIME = 10 * 10000;
const BACKOFF_TIMES = [0.5, 10, 30, 60, 5 * 60, 10 * 60] as const; // in seconds

// Define common types for reuse
type TimestampRecord = {
    past: number | undefined;
    future: number | undefined;
    pastPlaylistId: string | null | undefined;
    futurePlaylistId: string | null | undefined;
};

// Define return type for calculateRefetchInterval
type RefetchIntervalResult = {
    interval: number | false; // Only the types that React Query expects
    usedFutureTime: boolean;
};

// Constants for time calculations
const BUFFER_TIME_MS = 100; // Buffer to ensure we're past the program start time

// Helper function to process a channel item and update lowest timestamps
const processChannelItem = (
    item: Asset | AdditionalItems,
    currentTimestamp: number,
    lowestTimestamps: TimestampRecord,
    playlistId: string | null | undefined
): TimestampRecord => {
    if (item.type === ASSET_TYPE.CHANNEL) {
        const channelItem = item as Channel;
        if (channelItem.endsAt) {
            const updatedTimestamps = { ...lowestTimestamps };

            if (channelItem.endsAt > currentTimestamp) {
                // Future timestamp
                if (!updatedTimestamps.future || channelItem.endsAt < updatedTimestamps.future) {
                    updatedTimestamps.future = channelItem.endsAt;
                    updatedTimestamps.futurePlaylistId = playlistId;
                }
            } else if (!updatedTimestamps.past || channelItem.endsAt < updatedTimestamps.past) {
                // Past timestamp
                updatedTimestamps.past = channelItem.endsAt;
                updatedTimestamps.pastPlaylistId = playlistId;
            }

            return updatedTimestamps;
        }
    }

    return lowestTimestamps;
};

/**
 * Calculate milliseconds until the next program start time
 * @param futureTimestamp - Unix timestamp of future program
 * @param now - Current date for time calculations
 * @returns Milliseconds until next program or null if no future program
 */
const calculateMillisecondsUntilNextProgram = (
    futureTimestamp: number | undefined,
    now: Date
): number | false => {
    if (!futureTimestamp) {
        return false;
    }

    const nextProgramDate = new Date(futureTimestamp * 1000);
    return nextProgramDate.getTime() - now.getTime();
};

/**
 * Calculate backoff time in milliseconds based on backoff count
 * @param backoffCount - Current backoff attempt count
 * @returns Time in milliseconds for next backoff
 */
const calculateBackoffTimeMs = (backoffCount: number): number => {
    const backoffIndex = Math.min(backoffCount, BACKOFF_TIMES.length - 1);
    return BACKOFF_TIMES[backoffIndex] * 1000; // convert to ms
};

/**
 * Extract lowest timestamps from data
 * @param data - The query data to process
 * @returns Record containing lowest past and future timestamps
 */
const findLowestTimestamps = (data: { pages?: PageSection[][] } | undefined): TimestampRecord => {
    let lowestTimestamps: TimestampRecord = {
        past: undefined,
        future: undefined,
        pastPlaylistId: undefined,
        futurePlaylistId: undefined,
    };

    const now = new Date();
    const currentTimestamp = Math.floor(now.getTime() / 1000);

    // Process all data to find channel timestamps
    data?.pages?.forEach((page: PageSection[]) => {
        page?.forEach((section: PageSection) => {
            const { playlistId, items = [], additionalItems = [] } = section;

            // Process both items and additionalItems in the section
            items?.forEach((item) => {
                lowestTimestamps = processChannelItem(
                    item,
                    currentTimestamp,
                    lowestTimestamps,
                    playlistId
                );
            });

            additionalItems?.forEach((item) => {
                lowestTimestamps = processChannelItem(
                    item,
                    currentTimestamp,
                    lowestTimestamps,
                    playlistId
                );
            });
        });
    });

    return lowestTimestamps;
};

/**
 * Calculates optimal refetch interval based on channel program times
 * Export for testing purposes
 * @param data - Query data or timestamps directly
 * @param backoffCount - Current backoff attempt count
 * @param existingTimestamps - Optional pre-calculated timestamps
 * @returns Object containing interval and a flag indicating if future time was used
 */
export const calculateRefetchInterval = (
    data,
    backoffCount = 0,
    existingTimestamps?: TimestampRecord
): RefetchIntervalResult => {
    // Step 1: Find the lowest timestamps in past and future
    const now = new Date();
    const lowestTimestamps = existingTimestamps || findLowestTimestamps(data);

    // Step 2: Calculate times for both scenarios
    const hasPastContent = lowestTimestamps.past !== undefined;
    const hasFutureContent = lowestTimestamps.future !== undefined;

    // Calculate time until next program (if exists)
    const msUntilNextProgram = hasFutureContent
        ? calculateMillisecondsUntilNextProgram(lowestTimestamps.future, now)
        : false;

    // Calculate backoff time for past content
    const backoffTimeMs = hasPastContent ? calculateBackoffTimeMs(backoffCount) : 0;

    // Step 3: Determine refetch interval based on content types

    // Case 1: We have past content (stale)
    if (hasPastContent) {
        // Case 1A: We also have future content scheduled
        if (msUntilNextProgram !== false) {
            const nextProgramTimeWithBuffer = msUntilNextProgram + BUFFER_TIME_MS;

            // Include metadata about which strategy was chosen
            return {
                interval: Math.min(backoffTimeMs, nextProgramTimeWithBuffer),
                usedFutureTime: nextProgramTimeWithBuffer < backoffTimeMs,
            };
        }

        // Case 1B: Only past content, use backoff strategy
        return {
            interval: backoffTimeMs,
            usedFutureTime: false,
        };
    }

    // Case 2: We only have future content (no stale content)
    if (hasFutureContent && msUntilNextProgram !== false) {
        // Schedule update for next program time with buffer
        return {
            interval: msUntilNextProgram + BUFFER_TIME_MS,
            usedFutureTime: true,
        };
    }

    // Case 3: No program times found at all
    return {
        interval: false,
        usedFutureTime: false,
    };
};

export const getPlaylistQueryKey = (
    sectionsStructure: PageSection[],
    pageId?: string,
    selectedProfile?: Profile | null,
    language?: string
) => [QUERY_KEYS.playlists, pageId, sectionsStructure?.[0]?.id, selectedProfile?.id, language];

const usePlaylistsQuery = (
    sectionsStructure: PageSection[],
    pageId?: string,
    size?: number,
    selectedProfile?: Profile | null,
    hasScreenType?: boolean
) => {
    const { fetchPlaylist } = useContentData();
    const { i18n } = useTranslation();
    const { language } = i18n;
    const fetchSize = size || sectionsStructure.length;
    const { recordError } = useFirebase();
    const queryClient = useQueryClient();
    const [playlistToInvalidate, setPlaylistToInvalidate] = useState<string | null>(null);
    // Track backoff state when lowestEndsAt is in the past
    const backoffCountRef = useRef(0);

    return useInfiniteQuery<PageSection[]>(
        getPlaylistQueryKey(sectionsStructure, pageId, selectedProfile, language),
        async ({ pageParam }) => {
            if (playlistToInvalidate) {
                await queryClient.invalidateQueries([QUERY_KEYS.playlists, playlistToInvalidate]);
                setPlaylistToInvalidate(null);
            }

            const sections = await fetchPlaylist(sectionsStructure, fetchSize, pageParam || 0);

            // Filter out empty sections (no items and no additionalItems)
            return sections.filter(
                (section) =>
                    (section.items && section.items.length > 0) ||
                    (section.additionalItems && section.additionalItems.length > 0)
            );
        },
        {
            getNextPageParam: (_lastPage, pages) => {
                if (pages.length < sectionsStructure.length / fetchSize) {
                    return pages.length;
                }
                return undefined;
            },
            enabled: !!sectionsStructure.length,
            onError: (error) => {
                log(error);
                recordError?.(error);
            },
            staleTime: !hasScreenType ? 0 : INITIAL_STALE_TIME,
            // Refetch interval is calculated based on the lowest endsAt from all channels in all sections
            // If there are no channels with endsAt, refetchInterval will be false (no refetch)
            // If the lowest endsAt is in the past, we use a backoff strategy to avoid excessive requests
            // The backoff times are defined in BACKOFF_TIMES array
            // The backoff counter resets when no timestamps are in the past or when future time is used
            refetchInterval: (data, _query) => {
                // First, check if there are any channels with timestamps
                const lowestTimestamps = findLowestTimestamps(data);
                const hasChannelsWithTimestamps =
                    lowestTimestamps.past !== undefined || lowestTimestamps.future !== undefined;

                // Early return if no channels with timestamps are found
                if (!hasChannelsWithTimestamps) {
                    backoffCountRef.current = 0; // Reset backoff counter
                    return false;
                }

                // Calculate the refetch interval using the current backoff count and pre-calculated timestamps
                const result = calculateRefetchInterval(
                    data,
                    backoffCountRef.current,
                    lowestTimestamps
                );

                // Reset backoff counter if no items are in the past OR we're using future time as interval
                if (lowestTimestamps.past === undefined || result.usedFutureTime) {
                    backoffCountRef.current = 0;
                }
                // Otherwise, increment backoff counter if we're using backoff times and have past timestamps
                else if (
                    typeof result.interval === 'number' &&
                    lowestTimestamps.past !== undefined
                ) {
                    backoffCountRef.current = Math.min(
                        backoffCountRef.current + 1,
                        BACKOFF_TIMES.length - 1
                    );
                }

                if (typeof result.interval === 'number') {
                    // Determine which channel and playlist triggered the refetch
                    const isUsingFutureTimestamp = result.usedFutureTime;

                    const playlistId = isUsingFutureTimestamp
                        ? lowestTimestamps.futurePlaylistId
                        : lowestTimestamps.pastPlaylistId;

                    if (playlistId) {
                        // Store the playlist ID to invalidate
                        setPlaylistToInvalidate(playlistId);
                    }
                }

                // Return just the interval value that react-query expects (number or false)
                return result.interval;
            },
        }
    );
};

export default usePlaylistsQuery;
